import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sortBy } from "lodash";
import dayjs from "dayjs";
import { css } from "@emotion/react";
import { rgba } from "polished";
import { useServerSdk } from "../../RootNavigator/services/ServerSdk";
import ProjectInquiryStatus from "../../ProjectNavigator/ProjectScreen/inquiry/ProjectInquiryStatus";
import WithHelp from "../../RootNavigator/assistance/Help/WithHelp";
import { useWhoAmI } from "../../RootNavigator/services/WhoAmI";
import Table from "./Table";
import { PaymentViewProps } from ".";
import { InstructorAchievement } from "@hpo/client/models/Achievement";
import Spacer from "@hpo/client/utilities/Spacer";
import T from "@hpo/client/components/text/Text";
import Tabs, { Tab } from "@hpo/client/utilities/Tabs";
import { getProjectLabel } from "@hpo/client/utilities/helpers/ProjectHelper";
import { Analysis } from "@hpo/client/models/Analysis";
import {
  ReceiptDescriptor,
  getReceiptsForRequestTypeAndPaymentFragment,
} from "@hpo/client/utilities/Receipts";
import Sections from "@hpo/client/utilities/Sections";
import FilesField from "@hpo/client/utilities/fields/FilesField";
import TextField from "@hpo/client/utilities/fields/TextField";
import Columns from "@hpo/client/utilities/Columns";
import NumberField from "@hpo/client/utilities/fields/NumberField";
import { ReceiptWithAnalysis } from "@hpo/client/models/Receipt";
import { useSubmitCallback } from "@hpo/client/utilities/useSubmitCallback";
import Toasting from "@hpo/client/components/Toasting";
import IndicatorType, {
  IndicatorTypeLabels,
} from "@hpo/client/utilities/enums/IndicatorType";
import ErrorToast from "@hpo/client/utilities/ErrorToast";
import IndicatorOrigin from "@hpo/client/utilities/enums/IndicatorOrigin";
import { getMarkers, Marker } from "@hpo/client/models/Marker";
import Units from "@hpo/client/utilities/Units";
import { InstructorIndicator } from "@hpo/client/models/Indicator";
import Button from "@hpo/client/components/Button";
import FieldsForm from "@hpo/client/utilities/fields/FieldsForm";
import Role from "@hpo/client/utilities/enums/Role";
import Theme from "@hpo/client/utilities/Theme/client";
import SideBar from "@hpo/client/components/SideBar";
import MessageException from "@hpo/client/utilities/errors/MessageException";
import Divider from "@hpo/client/components/Divider";
import MarkerType from "@hpo/client/utilities/enums/MarkerType";

export default function PaymentReceipts(props: PaymentViewProps) {
  const { payment } = props;

  return (
    <Fragment>
      <T style="subtitle">Justificatifs</T>
      <Spacer />
      <Tabs showSingleTab>
        {payment.achievements
          .sort((achievmentA, achievmentB) => {
            return getProjectLabel(achievmentA.funding.project).localeCompare(
              getProjectLabel(achievmentB.funding.project),
            );
          })
          .map((a) => (
            <Tab
              title={getProjectLabel(a.funding.project)}
              id={a.id}
              key={a.id}
            >
              <AchievementView {...props} achievement={a} />
            </Tab>
          ))}
      </Tabs>
    </Fragment>
  );
}

type AchievementViewProps = PaymentViewProps & {
  achievement: InstructorAchievement;
  onRefresh: () => unknown;
};

function AchievementView(props: AchievementViewProps) {
  const { payment, achievement, onRefresh } = props;

  const server = useServerSdk();
  const whoami = useWhoAmI();
  const user = whoami.useUser();

  const descriptors = useMemo(() => {
    return getReceiptsForRequestTypeAndPaymentFragment(
      achievement.funding.project.type,
      payment.fragment,
    );
  }, [achievement.funding.project.type, payment.fragment]);

  const [indicatorNotes, setIndicatorNotes] = useState<string | null>(
    achievement.indicatorNotes,
  );

  const iAmInstructor = useMemo(() => {
    return user.roles.includes(Role.Instructor);
  }, [user.roles]);

  const [onAchievmentUpdate, achievmentUpdating] =
    useSubmitCallback(async () => {
      await server.updateAchievementIndicatorNotes(achievement.id, {
        indicatorNotes,
      });
      Toasting.success(
        "OK",
        "Le commentaire sur les indicateurs d'évaluation a bien été enregistré",
      );
      onRefresh();
    }, [indicatorNotes, achievement]);

  const [indicatorEdition, setIndicatorEdition] =
    useState<IndicatorEdit | null>(null);

  const onIndicatorEdited = useCallback(() => {
    onRefresh();
    setIndicatorEdition(null);
  }, [onRefresh]);

  return (
    <Fragment>
      <T style="section">Documents</T>
      <Spacer />
      <Sections>
        {descriptors.map((d) => {
          const receipt = achievement.receipts.find((r) => r.key === d.key);
          return (
            <ReceiptView
              {...props}
              descriptor={d}
              receipt={receipt || null}
              key={d.key}
            />
          );
        })}
      </Sections>
      <Spacer />
      <T style="section">Indicateurs d'évaluation</T>
      <Spacer />
      <Table>
        <IndicatorTablePart
          origin={IndicatorOrigin.Beneficiary}
          type={IndicatorType.Achievement}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
        <IndicatorTablePart
          origin={IndicatorOrigin.Beneficiary}
          type={IndicatorType.Result}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
        <IndicatorTablePart
          origin={IndicatorOrigin.Beneficiary}
          type={IndicatorType.Communication}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
        <IndicatorTablePart
          origin={IndicatorOrigin.Beneficiary}
          type={IndicatorType.Transfert}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
      </Table>
      <Spacer />
      <FieldsForm onSubmit={onAchievmentUpdate}>
        <TextField
          textArea
          value={indicatorNotes}
          label="Commentaire sur les indicateurs d'évaluation"
          onChange={setIndicatorNotes}
          disabled={!iAmInstructor}
        />
        {iAmInstructor && (
          <Fragment>
            <Spacer scale={0.5} />
            <Button
              submit
              label="Valider"
              loading={achievmentUpdating.running}
            />
          </Fragment>
        )}
        <ErrorToast error={achievmentUpdating.error} />
      </FieldsForm>
      <Spacer />
      <T style="section">Indicateurs d'impact</T>
      <Spacer />
      <Table>
        <IndicatorTablePart
          origin={IndicatorOrigin.Instructor}
          type={IndicatorType.Achievement}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
        <IndicatorTablePart
          origin={IndicatorOrigin.Instructor}
          type={IndicatorType.Result}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
        <IndicatorTablePart
          origin={IndicatorOrigin.Instructor}
          type={IndicatorType.Communication}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
        <IndicatorTablePart
          origin={IndicatorOrigin.Instructor}
          type={IndicatorType.Transfert}
          project={achievement.funding.project.id}
          indicators={achievement.funding.project.indicators}
          periods={achievement.funding.project.periods}
          onEditIndicator={setIndicatorEdition}
        />
      </Table>
      <SideBar
        visible={indicatorEdition !== null}
        onHide={() => setIndicatorEdition(null)}
      >
        {indicatorEdition ? (
          <IndicatorEdition {...indicatorEdition} onDone={onIndicatorEdited} />
        ) : null}
      </SideBar>
      <Spacer />
      <T style="section">Demande</T>
      <Spacer />
      <Columns columns={2}>
        <NumberField
          value={achievement.requestedAmount}
          label="Montant demandé"
          unit="euro"
          readonly
          allowZero
        />
        <TextField
          textArea
          value={achievement.requestNotes}
          label="Commentaire"
          readonly
        />
      </Columns>
      <Spacer />
    </Fragment>
  );
}

type IndicatorTablePartProps = {
  project: string;
  origin: IndicatorOrigin;
  type: IndicatorType;
  periods: Array<string>;
  indicators: Array<InstructorIndicator>;
  onEditIndicator: (edition: IndicatorEdit) => unknown;
};

function IndicatorTablePart(props: IndicatorTablePartProps) {
  const { project, origin, type, periods, indicators, onEditIndicator } = props;

  const filtered = useMemo(
    () => indicators.filter((i) => i.type === type && i.origin === origin),
    [type, indicators],
  );

  if (filtered.length === 0) return null;

  return (
    <Fragment>
      <thead>
        <tr>
          <th>{IndicatorTypeLabels[type]}</th>
          {periods.map((p) => (
            <th key={p}>{p}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filtered.map((i) => {
          return (
            <tr key={i.id}>
              <td>{i.label}</td>
              {periods.map((p) => {
                return (
                  <td key={p}>
                    <IndicatorMarkersOnPeriod
                      project={project}
                      indicator={i}
                      period={p}
                      onEditIndicator={onEditIndicator}
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </Fragment>
  );
}

type ReceiptViewProps = AchievementViewProps & {
  descriptor: ReceiptDescriptor;
  receipt: ReceiptWithAnalysis | null;
};

function ReceiptView(props: ReceiptViewProps) {
  const { payment, descriptor, receipt, onRefresh } = props;

  const server = useServerSdk();

  const saveAnalysis = useCallback(
    async (resourceId: string, analysis: Analysis) => {
      await server.saveAnalysis(resourceId, analysis);
      await onRefresh();
    },
    [server, onRefresh],
  );

  const [files, setFiles] = useState<Array<string> | null>(() =>
    receipt ? receipt.files : [],
  );

  const [onFilesChange, changeFiles] = useSubmitCallback(
    async (files: Array<string> | null) => {
      setFiles(files);
      if (!receipt) throw new Error("Missing receipt");
      await server.updateReceipt(receipt.id, { files });
      Toasting.success("Ok", `Documents mis à jour !`);
    },
    [setFiles, receipt, files],
  );

  return (
    <Sections.Section
      key={descriptor.key}
      id={descriptor.key}
      title={descriptor.label}
      right={
        receipt && (
          <ProjectInquiryStatus
            analysis={receipt.analysis}
            onChange={(analysis) => saveAnalysis(receipt.id, analysis)}
            readonly={payment.status !== "under-instruction"}
          />
        )
      }
    >
      <FilesField
        label="Documents"
        value={files}
        readonly={payment.status !== "under-instruction"}
        onChange={onFilesChange}
      />
      <Spacer />
      <TextField
        label="Notes du bénéficiaire"
        value={receipt ? receipt.notes : null}
        readonly
        textArea
      />
      <ErrorToast error={changeFiles.error} />
    </Sections.Section>
  );
}

type IndicatorMarkersOnPeriodProps = {
  project: string;
  indicator: InstructorIndicator;
  period: string;
  onEditIndicator: (edition: IndicatorEdit) => unknown;
};

export function IndicatorMarkersOnPeriod(props: IndicatorMarkersOnPeriodProps) {
  const { project, indicator, period, onEditIndicator } = props;

  const editableCss = css({
    ":hover": {
      background: rgba(Theme.color, 0.2),
      cursor: "pointer",
      outline: `3px solid ${rgba(Theme.color, 0.2)}`,
      borderRadius: 4,
    },
  });

  const { goal, actual, changes } = useIndicatorChanges(indicator, period);

  const goalStr = goal ? Units[indicator.unit].display(goal.value) : null;
  const actualStr = actual ? Units[indicator.unit].display(actual.value) : null;

  const goalNode = (
    <span
      css={editableCss}
      onClick={() =>
        onEditIndicator({ project, indicator, type: MarkerType.Goal, period })
      }
    >
      {goal ? Units[indicator.unit].display(goal.value) : "-"}
    </span>
  );
  const actualNode = (
    <span
      css={editableCss}
      onClick={() =>
        onEditIndicator({ project, indicator, type: MarkerType.Actual, period })
      }
    >
      {actual ? Units[indicator.unit].display(actual.value) : "-"}
    </span>
  );

  let str: ReactNode;
  if (goalStr && actualStr)
    str = (
      <T>
        {actualNode}
        <T style="minor">/{goalNode}</T>
      </T>
    );
  else if (goal) str = str = <T>{goalStr}</T>;
  else if (actual) {
    str = `${Units[indicator.unit].display(actual.value)} / ?`;
  }

  str = (
    <T>
      {actualNode}
      <T style="minor">/{goalNode}</T>
    </T>
  );

  const ref = useRef<HTMLSpanElement>(null);

  return (
    <Fragment>
      <WithHelp
        mouseoverRef={ref}
        title="Historique"
        text={
          <Fragment>
            <Spacer />
            <Table>
              <thead>
                <tr>
                  <th>Saisi le</th>
                  <th>par</th>
                  <th>Objectif</th>
                  <th>Réalisé</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((c, i) => (
                  <tr key={i}>
                    <td>{dayjs(c.at).format("LL")}</td>
                    <td>-</td>
                    <td>
                      {c.goal ? Units[indicator.unit].display(c.goal) : "-"}
                    </td>
                    <td>
                      {c.actual ? Units[indicator.unit].display(c.actual) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Fragment>
        }
      />
      <span ref={ref}>{str}</span>
    </Fragment>
  );
}

type IndicatorEdit = {
  project: string;
  indicator: InstructorIndicator;
  type: MarkerType;
  period: string;
};

type IndicatorEditionProps = IndicatorEdit & {
  onDone: () => unknown;
};

function IndicatorEdition(props: IndicatorEditionProps) {
  const { project, indicator, type, period, onDone } = props;

  const { goal, actual, changes } = useIndicatorChanges(indicator, period);

  const [value, setValue] = useState<number | null>(null);
  const [currentValue, setCurrentValue] = useState<number | null>(null);

  useEffect(() => {
    if (type === "actual" && actual) setCurrentValue(actual.value);
    else if (type === "goal" && goal) setCurrentValue(goal.value);
    else setCurrentValue(null);
    setValue(null);
  }, [goal, actual, type]);

  const server = useServerSdk();

  const [onSubmit, submission] = useSubmitCallback(async () => {
    if (value === null)
      throw new MessageException("Saisissez une valeur", null);
    await server.createMarker(project, indicator.id, type, period, value);
    onDone();
  }, [project, type, value, indicator, period, onDone]);

  return (
    <Fragment>
      <ErrorToast error={submission.error} />
      <T style="title">{indicator.label}</T>
      <Spacer />
      <T style="section">Historique des modifications</T>
      <Table frame>
        <thead>
          <tr>
            <th>Saisi le</th>
            <th>par</th>
            <th>Objectif</th>
            <th>Réalisé</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((c, i) => (
            <tr key={i}>
              <td>{dayjs(c.at).format("LL")}</td>
              <td>-</td>
              <td>{c.goal ? Units[indicator.unit].display(c.goal) : "-"}</td>
              <td>
                {c.actual ? Units[indicator.unit].display(c.actual) : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Divider />
      <Spacer />
      <FieldsForm onSubmit={onSubmit}>
        <NumberField
          label={`${type === "goal" ? "Changer l'objectif" : "Changer le réalisé"} pour ${period}`}
          value={value}
          unit={indicator.unit}
          onChange={setValue}
          allowZero
          help={
            currentValue !== null
              ? `Valeur actuelle : ${currentValue}`
              : undefined
          }
        />
        <Spacer />
        <Button submit label="Valider" disabled={value === null} />
      </FieldsForm>
    </Fragment>
  );
}

function useIndicatorChanges(indicator: InstructorIndicator, period: string) {
  const goals = useMemo(
    () => getMarkers(indicator.goals, period),
    [indicator, period],
  );
  const actuals = useMemo(
    () => getMarkers(indicator.actuals, period),
    [indicator, period],
  );
  const goal: Marker | null = goals[0] || null;
  const actual: Marker | null = actuals[0] || null;

  const changes = useMemo(() => {
    const all = [...goals, ...actuals];
    const sorted = sortBy(all, (m) => new Date(m.createdAt).valueOf());
    const events = sorted.reduce(
      (acc, item, i) => {
        if (item.type === "goal") {
          acc.push({
            at: item.createdAt,
            goal: item.value,
            actual: i === 0 ? null : acc[acc.length - 1].actual,
          });
        } else {
          acc.push({
            at: item.createdAt,
            goal: i === 0 ? null : acc[acc.length - 1].goal,
            actual: item.value,
          });
        }
        return acc;
      },
      [] as Array<{ at: string; goal: number | null; actual: number | null }>,
    );
    return events;
  }, [goals, actuals]);

  return { goal, actual, changes };
}
